import React, { useState, useEffect, useRef } from "react";
import { ActivityIndicator, FlatList, View } from "react-native";
import ScreenContainer from "../../components/ScreenContainer";
import ScrollableContainer from "../../components/ScrollableContainer";
import { useRoute, RouteProp } from "@react-navigation/native";
import AssessmentPane from "./AssessmentPane";
import VideoPane from "./VideoPane";
import { AssessmentNotice } from "./AsssessmentNotice";
import { Text, TouchableRipple } from "react-native-paper";
import PdrVideoPlayer from "./PdrVideoPlayer";
import { useMutation, useQuery } from "@apollo/client";
import { UPDATE_WATCH_PROGRESS, setNewWatchProgress, GET_VIDEO_DETAILS } from "./queries";
import { useErrorScroll } from "../../components/ErrorMessage/FormHelpers";
import { Assessment, AssessmentAttemptResult } from "./types";
import storage from "../../utils/storage";
import { FormData } from "./types";
import { useForm } from "react-hook-form";
import { CREATE_ASSESSMENT_ATTEMPT, updateAssessmentAttempts, updateMemberVideoStatusOnAssessmentAttempt } from "./queries";

function PaneToggle({
  panes,
  selected,
  select,
}: {
  selected: string;
  panes: { id: string; name: string }[];
  select(pane: { id: string }): void;
}) {
  return (
    <View className={`items-start px-6 py-6 sm:pt-0`}>
      <View className="flex-row w-full rounded-lg bg-sky-lighter justify-center items-center mx-auto">
        {panes.map((pane) => (
          <TouchableRipple
            key={pane.id}
            testID="pane-toggle"
            onPress={() => select(pane)}
            className={`flex-1 rounded-lg py-4 px-4 border items-center ${
              selected === pane.id ? "border-primary-light bg-sky-white" : "border-sky-lighter"
            }`}
          >
            <Text className={selected === pane.id ? "font-500 text-primary-light" : "font-400 text-ink-light"}>
              {pane.name}
            </Text>
          </TouchableRipple>
        ))}
      </View>
    </View>
  );
}

export default function VideoDetailsScreen() {
  const route = useRoute<RouteProp<{ params: { videoId: string } }, "params">>();
  const videoId = route.params.videoId;
  const { data, loading } = useQuery(GET_VIDEO_DETAILS, { variables: { videoId } });
  const [updateWatchProgress] = useMutation(UPDATE_WATCH_PROGRESS);
  const [createAssessmentAttempt, { loading: submitLoading }] = useMutation(CREATE_ASSESSMENT_ATTEMPT);

  const [videoPaneActive, setVideoPaneActive] = useState(true);
  const [canAccessAssessment, setCanAccessAssessment] = useState(false);
  const [showAssessmentNotice, setShowAssessmentNotice] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [assessmentResult, setAssessmentResult] = useState<AssessmentAttemptResult | null>(null);
  const [apiError, setApiError] = useState("");
  const [allAnswered, setAllAnswered] = useState(false);

  const flatListRef = useRef<FlatList>(null);

  const videoData = data?.videos[0] || {};
  const assessment = videoData?.assessment as Assessment;
  const storageKey = `assessmentFormData_${assessment?.id}`;
  const completedDate =
    videoData?.memberVideoStatus === "Completed"
      ? videoData?.assessmentAttempts?.find((attempt: { status: string }) => attempt.status === "passed")?.createdAt
      : null;

  const defaultValues = {
    answers:
      assessment?.questions?.map((q) => ({
        question_id: q.id,
        question_option_ids: [],
      })) || [],
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<FormData>({
    defaultValues,
    mode: "onChange",
  });

  const { Divider, scrollToField, onSubmitError } = useErrorScroll({ defaultValues, flatListRef });

  useEffect(() => {
    if (videoData) {
      setCanAccessAssessment(videoData.watchProgress?.watched || false);
    }
  }, [videoData]);

  useEffect(() => {
    const loadSavedFormData = async () => {
      if (!assessment?.id) return;
      try {
        const savedFormData = await storage.getItem(storageKey);
        if (savedFormData) {
          const parsedData = JSON.parse(savedFormData);
          parsedData.answers.forEach((answer: any, index: number) => {
            setValue(`answers.${index}.question_option_ids`, answer.question_option_ids);
          });
        }
      } catch (error) {
        console.error("Error loading saved form data:", error);
      }
    };

    loadSavedFormData();
  }, [assessment?.id, setValue, storageKey]);

  const onProgressUpdate = (highestWatchPosition: number, watched: boolean) => {
    setCanAccessAssessment(watched);
    updateWatchProgress({
      variables: { videoId: videoData.id, position: highestWatchPosition, watched: watched },
      update: (cache) => setNewWatchProgress(cache, videoData.id, highestWatchPosition, watched),
    });
  };

  const handleAnswerChange = async (index: number, value: string[]) => {
    setValue(`answers.${index}.question_option_ids`, value);

    const currentAnswers = watch("answers");
    setAllAnswered(assessment?.questions?.every((_, idx) => currentAnswers[idx]?.question_option_ids?.length > 0) ?? false);

    const formData = {
      answers: assessment.questions.map((q, i) => ({
        question_id: q.id,
        question_option_ids: i === index ? value : control._formValues.answers[i].question_option_ids,
      })),
    };
    try {
      await storage.setItem(storageKey, JSON.stringify(formData));
    } catch (error) {
      console.error("Error saving form data:", error);
    }
    setValue(`answers.${index}.question_option_ids`, value, { shouldValidate: true });
  };

  const onSubmit = async (data: FormData) => {
    try {
      const result = await createAssessmentAttempt({
        variables: {
          assessmentAttemptInput: {
            assessmentId: assessment.id,
            answers: data.answers.map((answer, index) => ({
              questionId: assessment.questions[index].id,
              questionOptionIds: answer.question_option_ids,
            })),
          },
        },
        update: (cache, { data }) => {
          updateAssessmentAttempts(cache, data?.createAssessmentAttempt?.assessmentAttempt?.status, videoData.id);
          updateMemberVideoStatusOnAssessmentAttempt(
            cache,
            videoData.id,
            data?.createAssessmentAttempt?.hasAttemptsRemaining,
            data?.createAssessmentAttempt?.assessmentAttempt?.status
          );
        },
      });

      if (result.data?.createAssessmentAttempt?.success) {
        setAssessmentResult(result.data.createAssessmentAttempt);
        setModalVisible(true);
        await storage.removeItem(storageKey);
      } else {
        setApiError(result.data?.createAssessmentAttempt?.message || "Something went wrong");
      }
    } catch (error) {
      console.error("Error submitting assessment:", error);
      setApiError((error as Error).message || "Something went wrong");
    }
  };

  const handleOnSubmitError = (errors: any) => {
    onSubmitError(errors);
    setApiError("An error occurred while submitting your answers. Please check your responses and try again.");
    const firstErrorField = Object.keys(errors.answers || {}).find((key) => errors.answers[key]?.question_option_ids);

    if (firstErrorField !== undefined) {
      scrollToField(`answers.${firstErrorField}.question_option_ids`);
    }
  };

  const onCloseAssessment = () => {
    setModalVisible(false);
    reset(defaultValues);
    setVideoPaneActive(true);
  };

  const onCloseModal = () => {
    setModalVisible(false);
    reset(defaultValues);
    setVideoPaneActive(false);
  };

  const selectPane = (pane: { id: string }) => {
    if (pane.id === "assessment" && videoData.memberVideoStatus === "Completed") {
      setShowAssessmentNotice(true);
    } else if (pane.id === "assessment" && videoData.memberVideoStatus === "Unavailable") {
      setShowAssessmentNotice(true);
    } else if (canAccessAssessment) {
      setVideoPaneActive(pane.id === "video");
    } else if (pane.id === "assessment" && !canAccessAssessment) {
      setShowAssessmentNotice(true);
    }
  };

  if (loading) {
    return (
      <ScreenContainer navigationTitle={"Video Detail"} noBackButton={false} testID="videos-list-screen">
        <ActivityIndicator size="large" className="flex-1 items-center justify-center bg-white mt-10" />
      </ScreenContainer>
    );
  }

  return (
    <ScreenContainer navigationTitle={"Video Detail"} noBackButton={false} testID="videos-details-screen">
      <ScrollableContainer ref={flatListRef}>
        <View className="aspect-video">
          <PdrVideoPlayer
            videoUrl={videoData?.url}
            watchProgress={videoData?.watchProgress}
            onProgressUpdate={onProgressUpdate}
            duration={videoData?.durationInSeconds}
          />
        </View>
        <PaneToggle
          panes={[
            { id: "video", name: "Video" },
            { id: "assessment", name: "Assessment" },
          ]}
          selected={videoPaneActive ? "video" : "assessment"}
          select={selectPane}
        />
        {videoPaneActive ? (
          <VideoPane videoData={videoData} selectPane={selectPane} completedDate={completedDate || ""} />
        ) : (
          <AssessmentPane
            videoData={videoData}
            setVideoPaneActive={setVideoPaneActive}
            control={control}
            errors={errors}
            handleAnswerChange={handleAnswerChange}
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            handleOnSubmitError={handleOnSubmitError}
            loading={submitLoading}
            apiError={apiError}
            setApiError={setApiError}
            allAnswered={allAnswered}
            modalVisible={modalVisible}
            assessmentResult={assessmentResult}
            onCloseAssessment={onCloseAssessment}
            onCloseModal={onCloseModal}
            Divider={Divider}
          />
        )}
        <AssessmentNotice
          videoStatus={videoData.memberVideoStatus}
          completedDate={completedDate || ""}
          visible={showAssessmentNotice}
          onDismiss={() => setShowAssessmentNotice(false)}
          onContinue={() => setShowAssessmentNotice(false)}
        />
      </ScrollableContainer>
    </ScreenContainer>
  );
}
